import { IModelItem } from "@yfiles/yfiles"
import { CHANGE_TYPE, LinqsetChanges, createLinqsetChanges } from "../common/linqset-change-model"
import { EDGE_TYPE, Linqset, LinqsetEdge, LinqsetNode, ORGANISATION_MODEL_KEY, isRoleSystem } from "../common/linqset-model"
import { NodeService } from "../model-services/node.service"

export interface ChangeEvent {
    type:CHANGE_TYPE
    items:IModelItem[]
    nodes: LinqsetNode[]
    edges: LinqsetEdge[]
    change: any
    doNotProcessGraph: boolean
}

const deleteNodeFromModel = (organisationLinqset: Linqset, linqset:Linqset, node:LinqsetNode) => {
    if (linqset.changes.deletedNodes.find(d => d._key === node._key)) return
    const previouslyAdded = linqset.changes.newNodes.find(n => n._key === node._key)
    if (previouslyAdded) {
        if (!isRoleSystem(node.superType) || !linqset.edges.find(e => e.targetKey === node._key || e.sourceKey === node._key)) {
            linqset.changes.newNodes = linqset.changes.newNodes.filter(n => n._key !== node._key)
            // if is a role or syste, node check that there are no edges connected to them.        
            linqset.nodes = linqset.nodes.filter(n => n._key !== node._key)
            if (isRoleSystem(node.superType)) organisationLinqset.nodes = organisationLinqset.nodes.filter(n => n._key !== node._key)
        }

    }else {
        // remove it if action or info or people otherwise remive if no edges attached. server will check for other edges across org.
        if (node.modelKey === linqset.modelKey) {
            linqset.changes.deletedNodes.push(node)
            linqset.nodes = linqset.nodes.filter(n => n._key !== node._key)
        }
    }
}

const deleteEdgeFromModel = (linqset:Linqset, edge:LinqsetEdge, changes:LinqsetChanges, organisationLinqset:Linqset) => {
    const previouslyAdded = linqset.changes.newEdges.find(e => e._key === edge._key)
    if (previouslyAdded) {
        // is in added list so remove from list
        linqset.changes.newEdges = linqset.changes.newEdges.filter(e => e._key !== edge._key)
        linqset.edges = linqset.edges.filter(e => e._key !== edge._key)
        if (edge.type === EDGE_TYPE.JOINS || edge.type === EDGE_TYPE.USED_IN) {
            organisationLinqset.edges = organisationLinqset.edges.filter(e => e._key !== edge._key)
        }
    }else if (!changes.deletedEdges.find(d => d._key === edge._key)) {
        // not already deleted so add to deleted list and remove from edit list
        changes.deletedEdges.push(edge)
        changes.edgeChanges = changes.edgeChanges.filter(e => e._key !== edge._key)
        linqset.edges = linqset.edges.filter(e => e._key !== edge._key)
        if (edge.type === EDGE_TYPE.JOINS || edge.type === EDGE_TYPE.USED_IN) {
            organisationLinqset.edges = organisationLinqset.edges.filter(e => e._key !== edge._key)
        }
    }
}

const addNodeToModel = (node:LinqsetNode, changes:LinqsetChanges) => {
    const previouslyDeleted = changes.deletedNodes.find(dn => dn._key === node._key)
    if (previouslyDeleted) {
        changes.deletedNodes = changes.deletedNodes.filter(dn => dn._key !== node._key)
    }else {
        changes.newNodes.push(node)
    }
}

export const applyChangeToModel = async (event:ChangeEvent, linqset:Linqset, organisationLinqset:Linqset, nodeService:NodeService) => {
    if (!linqset.changes) linqset.changes = createLinqsetChanges()

    const {changes} = linqset

    switch (event.type) {
        case CHANGE_TYPE.ADDED:
            if (event.nodes) {
                event.nodes.forEach(n => {
                    if (n.modelKey === ORGANISATION_MODEL_KEY) {
                        // is an organisation node. add it to org linqset if new
                        if (!organisationLinqset.getNode(n._key)) {
                            organisationLinqset.nodes.push(n)
                            addNodeToModel(n, changes)
                        }
                        if (!linqset.isOrganisationModel()) n.excluded = false
                    }
                    if (!linqset.nodes.find(ln => ln._key === n._key)) {
                        // not in linqset so add node is a model node
                        if (n.modelKey === linqset.model._key) {
                            // only add model nodes that belong to this model
                            addNodeToModel(n, changes)
                        }
                        linqset.nodes.push(n)
                    }
                })
            }
            if (event.edges) {
                event.edges.forEach(e => {
                    const previouslyDeleted = changes.deletedEdges.find(de => de._key === e._key)
                    if (previouslyDeleted) {
                        changes.deletedEdges = changes.deletedEdges.filter(de => de._key !== e._key)
                    }
                    const previouslyAdded = changes.newEdges.find(ne => ne._key === e._key)
                    if (!previouslyAdded) {
                        changes.newEdges.push(e)
                    }
                    if (!linqset.edges.find(le => le._key === e._key)) {
                        linqset.edges.push(e)
                    }
                    if ((e.type === EDGE_TYPE.JOINS || e.type === EDGE_TYPE.USEBY || e.type === EDGE_TYPE.FULFILS || e.type === EDGE_TYPE.PROVIDES  || e.type === EDGE_TYPE.USED_IN) && !organisationLinqset.edges.find(on => on._key === e._key)) {
                        organisationLinqset.edges.push(e)
                        if (!organisationLinqset.nodes.find(on => on._key === e.targetKey)) {
                            organisationLinqset.nodes.push(linqset.getNode(e.targetKey))
                        }
                    }
                })
            }
            break
        case CHANGE_TYPE.DELETED:
            if (event.edges) {
                event.edges.forEach(e => {
                    deleteEdgeFromModel(linqset, e, changes, organisationLinqset)
                })
            }

            if (event.nodes) {
                event.nodes.forEach(n => {
                    deleteNodeFromModel(organisationLinqset, linqset, n)
                })
            } 
            break
        case CHANGE_TYPE.CHANGED:
            if (event.nodes) {
                event.nodes.forEach(n => {
                    // if node is added then the change will already be in the added node
                    if (!changes.newNodes.find(c => c._key === n._key)) {
                        let existing = changes.nodeChanges.find(c => c._key === n._key)
                        if (existing) {
                            const properties = {...existing.properties, ...event.change.properties}
                            changes.nodeChanges = changes.nodeChanges.filter(c => c._key !== n._key)
                            changes.nodeChanges.push({...existing, ...event.change, properties})
                        }else changes.nodeChanges.push({_key:n._key, ...event.change})
                    }
                    // if they change external value, add or remove from org linqset
                    if (event.change.external !== undefined) {
                        if (event.change.external === true) {
                            if (!organisationLinqset.nodes.find(on => n._key === on._key))
                                organisationLinqset.nodes.push(n)
                        }else if (event.change.external === false) 
                            organisationLinqset.nodes = organisationLinqset.nodes.filter(on => on._key !== n._key)
                    }
                })
            }
            if (event.edges) {
                event.edges.forEach(e => {
                    const previouslyDeleted = changes.deletedEdges.find(de => de._key === e._key)
                    if (previouslyDeleted) {
                        changes.deletedEdges = changes.deletedEdges.filter(de => de._key !== e._key)
                    }
                    const previouslyAdded = changes.newEdges.find(ne => ne._key === e._key)
                    const previouslyEdited = changes.edgeChanges.find(ce => ce._key === e._key)

                    if (!previouslyAdded && !previouslyEdited) {
                        changes.edgeChanges.push(e)
                    }
                    if (!linqset.edges.find(le => le._key === e._key)) {
                        linqset.edges.push(e)
                    }
                })
            }
    }
}    

